const template = document.createElement("template");

template.innerHTML = `
<style>
  :host{
    --ctl-icon-size-sm: 16px;
    --ctl-icon-size-md: 20px;
    --ctl-icon-size-lg: 24px;

    display:inline-flex;
    vertical-align: middle;
    line-height: 0;
    color: currentColor;
  }

  .icon{
    width: var(--_size);
    height: var(--_size);
    display:inline-flex;
    align-items:center;
    justify-content:center;
  }

  svg{
    width: 100%;
    height: 100%;
    display:block;
    fill: currentColor;
  }
</style>

<span class="icon" part="icon"></span>
`;

function normalizeName(name) {
  return String(name || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-");
}

// Very small registry: name -> SVG string
// SVG strings should NOT include <svg width/height> hardcoded ideally.
// They may include viewBox and paths.
const ICON_REGISTRY = new Map();

/**
 * Register multiple icons at once.
 * @param {Record<string, string>} icons - { name: "<svg ...>...</svg>" }
 */
export function registerIcons(icons) {
  if (!icons || typeof icons !== "object") return;
  for (const [name, svg] of Object.entries(icons)) {
    if (!name || typeof svg !== "string") continue;
    ICON_REGISTRY.set(normalizeName(name), svg.trim());
  }
}

/**
 * Register a single icon.
 * @param {string} name
 * @param {string} svg
 */
export function registerIcon(name, svg) {
  if (!name || typeof svg !== "string") return;
  ICON_REGISTRY.set(normalizeName(name), svg.trim());
}

export class CtlIcon extends HTMLElement {
  static get observedAttributes() {
    return ["name", "size", "label", "aria-hidden"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
    this._wrap = this.shadowRoot.querySelector(".icon");
  }

  connectedCallback() {
    this._upgradeProperty("name");
    this._upgradeProperty("size");
    this._upgradeProperty("label");
    this._render();
  }

  attributeChangedCallback() {
    this._render();
  }

  _upgradeProperty(prop) {
    if (Object.prototype.hasOwnProperty.call(this, prop)) {
      const value = this[prop];
      delete this[prop];
      this[prop] = value;
    }
  }

  get name() {
    return this.getAttribute("name");
  }
  set name(v) {
    if (v == null) this.removeAttribute("name");
    else this.setAttribute("name", v);
  }

  get size() {
    return this.getAttribute("size") || "md";
  }
  set size(v) {
    if (v == null) this.removeAttribute("size");
    else this.setAttribute("size", v);
  }

  get label() {
    return this.getAttribute("label");
  }
  set label(v) {
    if (v == null) this.removeAttribute("label");
    else this.setAttribute("label", v);
  }

  _render() {
    const name = normalizeName(this.getAttribute("name"));
    const size = (this.getAttribute("size") || "md").toLowerCase();
    const label = this.getAttribute("label");
    const ariaHiddenAttr = this.getAttribute("aria-hidden");
    const ariaHidden =
      ariaHiddenAttr === "" ||
      ariaHiddenAttr === "true" ||
      (!label && ariaHiddenAttr !== "false");

    // Size mapping
    const sizeVar =
      size === "sm" ? "var(--ctl-icon-size-sm)" :
      size === "lg" ? "var(--ctl-icon-size-lg)" :
      "var(--ctl-icon-size-md)";

    this.style.setProperty("--_size", sizeVar);

    // A11y
    // If label is provided: expose as img with aria-label
    // else default to aria-hidden=true
    // Only update attributes if they need to change to prevent infinite loop
    if (ariaHidden) {
      if (this.getAttribute("aria-hidden") !== "true") {
        this.setAttribute("aria-hidden", "true");
      }
      this.removeAttribute("role");
      this.removeAttribute("aria-label");
    } else {
      const newAriaLabel = label || name || "icon";
      if (this.getAttribute("role") !== "img") {
        this.setAttribute("role", "img");
      }
      if (this.getAttribute("aria-label") !== newAriaLabel) {
        this.setAttribute("aria-label", newAriaLabel);
      }
      this.removeAttribute("aria-hidden");
    }

    // Render svg
    const svg = ICON_REGISTRY.get(name);
    if (!svg) {
      // graceful fallback: empty box
      this._wrap.innerHTML = `<svg viewBox="0 0 24 24" aria-hidden="true"></svg>`;
      return;
    }

    // Ensure the svg uses currentColor if possible:
    // We wrap raw SVG; consumers should author icons using fill="currentColor" or no fill.
    this._wrap.innerHTML = svg;

    // Safety: ensure the first svg is present
    const svgEl = this._wrap.querySelector("svg");
    if (svgEl && !svgEl.getAttribute("viewBox")) {
      // Not required, but helps avoid layout issues if someone registers an svg lacking viewBox
      svgEl.setAttribute("viewBox", "0 0 24 24");
    }
    // Avoid focus in IE/Edge legacy patterns (still harmless)
    if (svgEl) svgEl.setAttribute("focusable", "false");
  }
}

customElements.define("ctl-icon", CtlIcon);
